import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { PMField } from "../../utils/PMField";
import { dispatchWithToast } from "../../Utils";
import { fetchCaseTypes } from "../../../api/case";
import { createCaseAsync } from "../../../slices/case";
import PMCombobox from "../../utils/PMCombobox";
import { fetchUsers } from "../../../api/users";
import { customerLockBlocksSubmit } from "../../../contexts/CustomerLockTracker";
import { priorityOptions } from "../quality/deviations/utils";
import { PMDialog } from "../../utils/PMDialog";
import { CustomerPicker, ContactPicker } from "../../utils/pickers";
import { PMDatePicker } from "../../utils/PMDatePicker";

function mapIdTitleOptions(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row, i) => ({
    value: row.id,
    title: row.title || row.displayTitle || row.name,
    order: i,
  }));
}

/**
 * Create-only case dialog. Existing cases open the case page — editing lives there.
 */
export default function CaseDialog({ onRefresh = () => {}, ...props }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);

  const {
    open,
    onClose,
    selected,
    customer,
    project,
    report,
    contact,
    title,
    itemTags,
    navigateOnCreate = true,
  } = props;

  const existingCaseId = selected?.id || null;

  const [userOptions, setUserOptions] = useState([]);
  const [caseTypeOptions, setCaseTypeOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [pickedCustomer, setPickedCustomer] = useState(customer || null);
  const [pickedContact, setPickedContact] = useState(contact || null);

  useEffect(() => {
    if (!open || !existingCaseId) return;
    onClose?.();
    navigate(`/dashboard/cases/${existingCaseId}`);
  }, [open, existingCaseId, navigate, onClose]);

  useEffect(() => {
    if (!open || existingCaseId) return;
    setPickedCustomer(customer || null);
    setPickedContact(contact || null);
    // Seed from props when the dialog opens or the *id* changes. Callers such
    // as report case-create pass `report.customer` as a new object on each
    // parent render — depending on object identity would wipe a contact the
    // user just picked while Formik still holds the new contact_id.
  }, [open, existingCaseId, customer?.id, contact?.id]);

  useEffect(() => {
    if (!open || existingCaseId) return;
    let cancelled = false;
    setOptionsLoading(true);

    Promise.all([
      fetchCaseTypes()
        .then((res) => mapIdTitleOptions(res?.data || res || []))
        .catch(() => []),
      fetchUsers({
        filters: [{ name: "role", value: "case" }],
        page: 1,
        query: "",
        sort: "desc",
        sortBy: "id",
        view: "all",
        fields: "id,name,displayTitle",
      })
        .then((res) =>
          mapIdTitleOptions(
            (res?.data || []).map((row) => ({
              id: row.id,
              title: row.displayTitle || row.name,
            }))
          )
        )
        .catch(() => []),
    ]).then(([types, users]) => {
      if (cancelled) return;
      setCaseTypeOptions(types);
      setUserOptions(users);
      setOptionsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [open, existingCaseId]);

  if (existingCaseId) {
    return null;
  }

  const handleDescriptionChange = (e, formik) => {
    const items = [
      {
        description: e.target.value,
        ...(itemTags ? { tags: itemTags } : {}),
      },
    ];
    formik.setFieldValue("items", items);
  };

  return (
    <PMDialog
      isLoading={optionsLoading}
      size="2xl"
      open={open}
      onClose={onClose}
      title={t("cases.create.dialog_title")}
      closeOnOutsideClick={false}
    >
      <Formik
        enableReinitialize
        initialValues={{
          customer_id: customer?.id || null,
          contact_id: contact?.id || null,
          report_id: report?.id || null,
          project_id: project?.id || null,
          title: title || "",
          case_type_id: "",
          module: props?.module || null,
          module_param: props?.module_param || null,
          user_assigned_id: currentUser?.id || null,
          priority: "1",
          due_date: "",
          items: [{ description: "" }],
          submit: null,
        }}
        validationSchema={Yup.object({
          case_type_id: Yup.string().required(t("cases.create.type_required")),
        })}
        onSubmit={(values, helpers) => {
          try {
            dispatchWithToast(
              dispatch(createCaseAsync(values)),
              (data) => {
                onRefresh?.(data);
                onClose?.();
                const createdId = data?.id || data?.data?.id;
                if (navigateOnCreate && createdId) {
                  navigate(`/dashboard/cases/${createdId}`);
                }
              },
              null
            );
            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
          } catch (err) {
            console.error("Error in case dialog:", err);
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: err.message });
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form>
            <p className="mb-5 text-sm text-slate-500">
              {t("cases.create.dialog_hint")}
            </p>

            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <PMField
                  name="title"
                  type="text"
                  label={t("Tittel")}
                  {...formik}
                />
              </div>

              {!customer?.id && (
                <div className="col-span-6 sm:col-span-3">
                  <CustomerPicker
                    value={pickedCustomer}
                    setFieldValue={formik.setFieldValue}
                    disabled={formik.isSubmitting}
                    error={formik.touched.customer_id ? formik.errors.customer_id : undefined}
                    onChange={(event) => {
                      const row = event?.target?.data || null;
                      setPickedCustomer(row);
                      if (String(row?.id ?? "") !== String(formik.values.customer_id ?? "")) {
                        setPickedContact(null);
                        formik.setFieldValue("contact_id", null);
                      }
                    }}
                  />
                </div>
              )}

              <div className="col-span-6 sm:col-span-3">
                <ContactPicker
                  value={pickedContact}
                  customer={
                    pickedCustomer?.id != null
                      ? pickedCustomer
                      : formik.values.customer_id
                        ? { id: formik.values.customer_id }
                        : null
                  }
                  setFieldValue={formik.setFieldValue}
                  disabled={formik.isSubmitting}
                  error={formik.touched.contact_id ? formik.errors.contact_id : undefined}
                  onChange={(event) => setPickedContact(event?.target?.data || null)}
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <PMCombobox
                  name="case_type_id"
                  label={t("Case Type")}
                  options={caseTypeOptions}
                  {...formik}
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <PMCombobox
                  name="priority"
                  label={t("Priority")}
                  options={priorityOptions}
                  {...formik}
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <PMCombobox
                  name="user_assigned_id"
                  label={t("cases.create.user_assigned")}
                  options={userOptions}
                  {...formik}
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <PMDatePicker
                  name="due_date"
                  label={t("Forfallsdato")}
                  value={formik.values.due_date}
                  onChange={(event) =>
                    formik.setFieldValue("due_date", event?.target?.value || "")
                  }
                />
              </div>

              <div className="col-span-6">
                <label htmlFor="case-create-description" className="mb-1 block text-sm font-medium text-slate-700">
                  {t("Description")}
                </label>
                <textarea
                  id="case-create-description"
                  rows={4}
                  name="description"
                  className="block w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500"
                  placeholder={t("cases.create.description_placeholder")}
                  value={formik.values.items[0]?.description || ""}
                  onChange={(e) => handleDescriptionChange(e, formik)}
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                onClick={onClose}
              >
                {t("Close")}
              </button>
              <button
                type="button"
                className="inline-flex justify-center rounded-md bg-[#1463C7] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                onClick={formik.submitForm}
                disabled={
                  formik.isSubmitting ||
                  customerLockBlocksSubmit(formik.values)
                }
              >
                {t("Create")}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
}
